<script setup lang="ts">
import * as Sentry from '@sentry/browser'
import { screenModule } from '@/store/screen/module'
import { stripeModule } from '@/store/stripe/module'
import { giftModule } from '@/store/gift/module'
import UIKit from '@pocketprep/ui-kit'
import type { StripeEmbeddedCheckout } from '@stripe/stripe-js'
import { buildUTMParamString } from '@/store/utils'
import type { TGiftTier } from '@/store/gift/state'
import {
    computed,
    ref,
    nextTick,
    onBeforeUnmount,
    onMounted,
} from 'vue'
import { useRouter } from 'vue-router'
import { analyticsModule } from '@/store/analytics/module'
import { isSupportWindow } from '@/utils'

const router = useRouter()

const {
    Button: PocketButton,
    Icon,
    Link: PocketLink,
    Tag,
    Errors,
} = UIKit

const currentStep = ref(1)
const checkout = ref<StripeEmbeddedCheckout | null>(null)
const checkoutError = ref('')

const selectedGiftTier = ref<TGiftTier>('gift-3-month')

const giftOptions = computed(() => {
    const oneMonthAmount = giftPriceAmount('gift-1-month')
    const threeMonthAmount = giftPriceAmount('gift-3-month')
    const threeMonthSavings = (oneMonthAmount && threeMonthAmount)
        ? Math.round((1 - threeMonthAmount / (3 * oneMonthAmount)) * 100)
        : 0

    return [
        {
            tier: 'gift-1-month' as const,
            name: '1 Month',
            regularPrice: oneMonthAmount ? (oneMonthAmount / 100).toFixed(2) : undefined,
        },
        {
            tier: 'gift-3-month' as const,
            name: '3 Months',
            regularPrice: threeMonthAmount ? (threeMonthAmount / 100).toFixed(2) : undefined,
            tag: threeMonthSavings ? `Save ${threeMonthSavings}%` : undefined,
        },
    ]
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

onMounted(async () => {
    analyticsModule.actions.amplitudeTrack('Gift Viewed')

    await stripeModule.actions.fetchStripePrices()
})

onBeforeUnmount(() => {
    destroyCheckout()
})

// Gift Stripe prices have the gift tier as lookup keys
const giftPriceAmount = (tier: TGiftTier) => {
    const priceAmount = stripeModule.getters.getStripePrices()
        .find(price => price.lookup_key === tier)?.unit_amount
    return priceAmount || null
}

const goToStep = (step: number) => {
    currentStep.value = step
    scrollToTop()
    focusTitle()
}

const selectGiftOption = (tier: TGiftTier) => {
    selectedGiftTier.value = tier
}

const addPaymentBtnClicked = async () => {
    goToStep(2)
    await nextTick()
    await mountCheckout()
}

const mountCheckout = async () => {
    try {
        const tier = selectedGiftTier.value
        if (!tier) {
            throw new Error('Gift.mountCheckout: Missing gift tier')
        }

        destroyCheckout()
        checkoutError.value = ''
        const { stripe } = await stripeModule.actions.loadStripeState()
        if (!stripe) {
            throw new Error('Gift.mountCheckout: Missing Stripe')
        }
        const { clientSecret } = await giftModule.actions.createGiftCheckoutSession({ tier })
        if (!clientSecret) {
            throw new Error('Gift.mountCheckout: Missing clientSecret')
        }

        // Stripe redirects to the Gift Purchased screen itself, so there is no onComplete
        checkout.value = await stripe.initEmbeddedCheckout({ clientSecret })
        checkout.value.mount('#gift-checkout')
    } catch (err) {
        Sentry.captureException(err)
        checkoutError.value = 'Error loading the payment form. '
            + 'Try again, or contact support if the issue persists.'
    }
}

const destroyCheckout = () => {
    checkout.value?.destroy()
    checkout.value = null
}

const backBtnClicked = () => {
    if (currentStep.value === 2) {
        destroyCheckout()
        checkoutError.value = ''
        goToStep(1)
    } else {
        router.back()
    }
}

const scrollToTop = () => {
    // document body is the mobile scroll container
    if (breakpoint.value === 'black-bear') {
        document.body.scrollTo(0, 0)
    } else {
        document.querySelector('.study__body')?.scrollTo(0, 0)
    }
}

const focusTitle = () => {
    setTimeout(() => {
        const titleEl = document.querySelector('.gift__title') as HTMLElement
        titleEl?.focus()
    }, 1)
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}
</script>

<template>
    <div class="gift">
        <template v-if="currentStep === 1">
            <img
                class="gift__lower-left-blob"
                src="@/assets/gift/lower-left-blob.svg"
                alt=""
            >
            <img
                class="gift__upper-right-blob"
                src="@/assets/gift/upper-right-blob.svg"
                alt=""
            >
        </template>
        <div class="gift__head">
            <div class="gift__left">
                <div
                    class="gift__back-btn"
                    tabindex="0"
                    role="button"
                    aria-label="Back"
                    @keydown.enter="backBtnClicked"
                    @click="backBtnClicked"
                    @mousedown.prevent
                >
                    <Icon type="arrow" />
                </div>
            </div>
            <div class="gift__steps">
                <div
                    class="gift__step"
                    :class="{
                        'gift__step--active': currentStep <= 1,
                        'gift__step--complete': currentStep > 1,

                    }"
                >
                    <span v-if="currentStep <= 1" aria-hidden="true">1</span>
                    <span v-else-if="currentStep > 1" aria-hidden="true"><Icon type="check" /></span>
                    <div>
                        <span class="gift__sr-only">{{
                            currentStep <= 1 ? 'Current step: ' : 'Completed step: '
                        }}</span>Select Gift
                    </div>
                </div>
                <div
                    class="gift__step"
                    :class="{
                        'gift__step--active': currentStep === 2,
                        'gift__step--complete': currentStep > 2,
                    }"
                >
                    <span v-if="currentStep <= 2" aria-hidden="true">2</span>
                    <span v-else-if="currentStep > 2" aria-hidden="true"><Icon type="check" /></span>
                    <div>
                        <span class="gift__sr-only">{{
                            currentStep === 2 ? 'Current step: ' : 'Step 2: '
                        }}</span>Add Payment
                    </div>
                </div>
            </div>
            <div class="gift__links">
                <PocketLink
                    class="gift__help-link"
                    @click="showIntercom"
                    @keydown.enter="showIntercom"
                    @mousedown.prevent
                >
                    Help
                </PocketLink>
            </div>
        </div>
        <main class="gift__main">
            <div class="gift__step-info">
                Step {{ currentStep }} of 2
            </div>
            <h1
                class="gift__title"
            >
                {{ currentStep === 1 ? 'Select Gift' : 'Add Payment & Review' }}
            </h1>
            <div v-if="currentStep === 1" class="gift__select-gift">
                <div class="gift__subtitle">
                    Give the gift of Pocket Prep!
                </div>
                <div class="gift__options" role="radiogroup">
                    <div
                        v-for="giftOption in giftOptions"
                        :key="giftOption.tier"
                        class="gift__option"
                        :class="{
                            'gift__option--selected': giftOption.tier === selectedGiftTier,
                            'gift__option--another-option': giftOption.tier !== selectedGiftTier
                        }"
                        tabindex="0"
                        role="radio"
                        :aria-checked="giftOption.tier === selectedGiftTier"
                        @keydown.enter="selectGiftOption(giftOption.tier)"
                        @keydown.space.prevent="selectGiftOption(giftOption.tier)"
                        @click="selectGiftOption(giftOption.tier)"
                        @mousedown.prevent
                    >
                        <Tag
                            v-if="giftOption.tag"
                            class="gift__tag"
                            :size="breakpoint !== 'black-bear' ? 'medium' : 'small'"
                            :text-color="'#fff'"
                            :background-color="giftOption.tier === selectedGiftTier ? '#0064ff': 'rgba(71, 89, 103, 1)'"
                            :tag-border="'none'"
                        >
                            <template #tagMessage>
                                {{ giftOption.tag }}
                            </template>
                        </Tag>
                        <div class="gift__option-top-info">
                            <div v-if="giftOption.name" class="gift__option-duration">
                                {{ giftOption.name }}
                            </div>
                            <div v-if="giftOption.regularPrice" class="gift__option-price-label">
                                ${{ giftOption.regularPrice }}
                            </div>
                        </div>
                        <span class="gift__option-divider" />
                        <div v-if="giftOption.name" class="gift__option-details">
                            <div class="gift__option-detail">
                                {{ giftOption.name.toLowerCase() }} of Premium access to ALL exams
                            </div>
                            <div class="gift__option-detail">
                                One-time payment
                            </div>
                        </div>
                    </div>
                </div>
                <div class="gift__terms-conditions">
                    <PocketLink
                        :href="`https://www.pocketprep.com/terms-of-service/?${
                            buildUTMParamString({
                                utm_campaign: 'app_settings',
                                utm_content: 'gift_terms',
                            })
                        }`"
                        target="_blank"
                        class="gift__terms-conditions-link"
                        @mousedown.prevent
                    >
                        Gift Terms and Conditions
                        <span class="gift__sr-only">(opens in a new tab)</span>
                        <Icon type="externalLink" />
                    </PocketLink>
                </div>
                <PocketButton
                    class="gift__add-payment-btn"
                    :disabled="!selectedGiftTier"
                    @click="addPaymentBtnClicked"
                >
                    Add Payment<Icon class="gift__add-payment-btn-arrow" type="arrow" />
                </PocketButton>
                <img
                    class="gift__brain-image"
                    src="@/assets/brain/brain-gift.png"
                    alt="Cartoon-style pink brain character with glasses holding a gift"
                >
            </div>
            <div v-else class="gift__add-payment">
                <div v-if="checkoutError" class="gift__checkout-error">
                    <Errors :errors="[ checkoutError ]" />
                    <PocketButton @click="mountCheckout">
                        Try Again
                    </PocketButton>
                </div>
                <div id="gift-checkout" class="gift__checkout" />
            </div>
        </main>
    </div>
</template>

<style lang="scss" scoped>
.gift {
    position: relative;
    width: 100%;
    min-height: 100%;
    background-color: $gray-background;

    &__lower-left-blob {
        position: absolute;
        left: 0;
        bottom: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__upper-right-blob {
        position: absolute;
        top: 56px;
        right: 0;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__head {
        display: flex;
        flex-wrap: wrap;
        row-gap: 8px;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        min-height: 56px;
        padding: 8px 20px 8px 15px;
        box-sizing: border-box;
        border-bottom: 1px solid rgba($pewter, 0.3);
        background-color: $white;
    }

    &__left {
        display: flex;
    }

    &__back-btn {
        display: flex;
        color: $brand-blue;
        cursor: pointer;
        outline: none;
        position: relative;

        &:hover,
        &:focus {
            color: $brand-black;
        }

        &:focus::before {
            content: "";
            border: 1px solid $brand-black;
            width: 27px;
            height: 26px;
            top: -6px;
            left: -4px;
            border-radius: 4px;
            box-sizing: border-box;
            position: absolute;
        }

        svg {
            transform: rotate(180deg);
            width: 19px;
            height: 14px;
        }
    }

    &__steps {
        display: flex;
        margin-left: 15px;
    }

    &__step {
        font-size: 13px;
        line-height: 18px;
        color: $slate-01;
        margin-right: 25px;
        position: relative;

        @include breakpoint(black-bear) {
            margin-right: 24px;
        }

        &::after {
            content: '';
            right: -15px;
            top: 6px;
            width: 5px;
            height: 10px;
            position: absolute;
            background: url('@/assets/onboarding/stepper.svg') no-repeat center center;
        }

        &:last-child {
            margin-right: 0;

            &::after {
                display: none;
            }
        }

        > span {
            font-weight: 700;
            width: 22px;
            line-height: 22px;
            text-align: center;
            margin: 0;
            display: inline-block;
        }

        div {
            display: inline-block;
        }

        &--active {
            color: $brand-black;

            span {
                color: $brand-blue;
            }
        }

        &--complete {
            > span {
                margin: 0 2px 0 0;

                svg {
                    width: 16px;
                    height: 13px;
                }
            }

            div {
                text-decoration: line-through;
            }
        }
    }

    &__help-link {
        line-height: 20px;
    }

    &__sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        white-space: nowrap;
        border: 0;
    }

    &__select-gift {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__step-info {
        font-size: 13px;
        line-height: 18px;
        text-align: center;
        color: $slate-03;
        padding-top: 36px;
        margin-bottom: 8px;
    }

    &__title {
        font-size: 26px;
        outline: none;
        line-height: 36px;
        font-weight: 700;
        text-align: center;
        margin: 0;
        padding: 0 25px;
    }

    &__subtitle {
        color: $ash;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 40px;
        margin-top: 4px;
    }

    &__options {
        display: flex;
        flex-direction: row;
        align-items: center;
        box-sizing: border-box;
        justify-content: center;

        @include breakpoint(black-bear) {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 400px;
            padding: 0 17.5px;
        }
    }

    &__option {
        display: flex;
        position: relative;
        flex-direction: column;
        align-items: center;
        box-sizing: border-box;
        border-radius: 5px;
        border: 1px solid rgba($pewter, 0.85);
        box-shadow: 0px 1px 4px 0px rgb(71, 89, 103, 0.30);
        background: $white;
        width: 326px;
        padding: 19px 0 13px;
        user-select: none;
        margin-bottom: 40px;
        outline: none;

        &:not(:first-child) {
            margin-left: 20px;

            @include breakpoint(black-bear) {
                margin-left: 0;
            }
        }

        &::before {
            content: '';
            display: none;
            left: -2px;
            top: -2px;
            width: 100%;
            height: 100%;
            border-radius: 5px;
            position: absolute;
        }

        &--another-option {
            cursor: pointer;
            &:hover::before {
                display: block;
                border: 2px solid $slate;
                border-radius: 5px;
            }

            &:focus::before {
                display: block;
                left: -1px;
                top: -1px;
                border: 1px solid $brand-blue;
                border-radius: 5px;
            }
        }

        &--selected {
            background-color: $barely-blue;
            &::before {
                display: block;
                left: -2px !important;
                top: -2px !important;
                border: 2px solid $brand-blue !important;
                border-radius: 5px;
            }
        }

        @include breakpoint(black-bear) {
            width: 100%;
            align-items: flex-start;
            padding: 16px 14px 14px;
            gap: 8px;
            margin-bottom: 24px;

            &:last-child {
                margin-bottom: 40px;
            }
        }

    }

    &__tag {
        position: absolute;
        top: -10px;
        right: 15px;
        height: 18px;
        padding: 1px 3px;
        box-sizing: border-box;

        @include breakpoint(black-bear) {
            top: -12px;
            right: 7px;
        }
    }

    &__option-top-info {
        @include breakpoint(black-bear) {
            display: flex;
            width: 100%;
            justify-content: space-between;
        }

        @include breakpoint(koala-bear) {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }
    }

    &__option-duration {
        color: $ash;
        text-align: center;
        font-size: 20px;
        font-weight: 500;
        line-height: 27px;
        margin-bottom: 4px;

        @include breakpoint(black-bear) {
            margin: 0;
        }
    }

    &__option-price-label {
        display: flex;
        flex-direction: row;
        justify-content: center;
        white-space: nowrap;
        color: $brand-black;
        font-size: 23px;
        font-weight: 600;
        line-height: 24px;

        @include breakpoint(black-bear) {
            margin: 0;
            line-height: 26px;
        }
    }

    &__option-divider {
        width: 285px;
        height: 1px;
        background-color: $fog;
        margin-top: 13px;
        margin-bottom: 13px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__option-details {
        display: flex;
        flex-direction: column;
        align-items: center;
        line-height: 19px;

        @include breakpoint(black-bear) {
            align-items: flex-start;
        }
    }

    &__option-detail {
        color: $ash;
        text-align: center;
        font-size: 14px;
        font-weight: 500;

        @include breakpoint(black-bear) {
            text-align: left;
        }
    }

    &__terms-conditions {
        display: flex;
        align-items: center;
        margin-bottom: 40px;

        svg {
            width: 10px;
            height: 10px;
        }
    }

    &__terms-conditions-link {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;
    }

    &__add-payment-btn {
        margin-bottom: 40px;
    }

    &__add-payment-btn-arrow {
        margin-left: 8px;
    }

    &__brain-image {
        width: 235px;
        height: 235px;
    }

    &__add-payment {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 40px 17.5px;
    }

    &__checkout-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        margin-bottom: 40px;
    }

    &__checkout {
        width: 100%;

        // Wide enough for Stripe's two-column layout; it falls back to one column on its own
        max-width: 1200px;
    }
}
</style>
